import type { TransportSelectorContext, TransportSelectorFilters } from '../types'
import type { TransportSelectorUrlBuilder } from '../urlBuilder/transportSelectorUrlBuilder'
import type { SelfDriveApiRawResponse } from './types'
import type { FilterSelection } from '@/scopes/prices/domain/filters/types'
import type { SelfDriveSelectorResponse } from '@/scopes/prices/domain/transports/types'
import defaultFetch from 'isomorphic-unfetch'
import { createBrowserQueryString } from '@/scopes/common/infrastructure/query-strings/browserQueryString'
import { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types/browserQueryString'
import { createTransportSelectorUrlBuilder, TransportType } from '../urlBuilder/transportSelectorUrlBuilder'
import {
  createSelfDriveSelectorResponseMapper,
  type SelfDriveSelectorResponseMapper,
} from './selfDriveSelectorResponseMapper'

interface Dependencies {
  fetch: typeof defaultFetch
  selfDriveSelectorResponseMapper: SelfDriveSelectorResponseMapper
  transportSelectorUrlBuilder: TransportSelectorUrlBuilder
  browserQuery: BrowserQueryString
}

export interface SelfDriveSelectorApiClient {
  getSelfDrive: (
    criteria: TransportSelectorContext & { filterSelection: FilterSelection } & TransportSelectorFilters
  ) => Promise<SelfDriveSelectorResponse>
}

export function createSelfDriveSelectorApiClient(dependencies: Partial<Dependencies> = {}) {
  const {
    fetch = defaultFetch,
    selfDriveSelectorResponseMapper = createSelfDriveSelectorResponseMapper(),
    browserQuery = createBrowserQueryString(),
    transportSelectorUrlBuilder = createTransportSelectorUrlBuilder({ browserQuery }),
  } = dependencies as Dependencies

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }

  async function getSelfDrive(
    criteria: TransportSelectorContext & { filterSelection: FilterSelection } & TransportSelectorFilters,
  ) {
    const url = transportSelectorUrlBuilder.buildUrl(criteria, TransportType.SelfDrive)

    const response = await fetch(url, fetchOptions)

    const data = (await response.json()) as SelfDriveApiRawResponse

    return selfDriveSelectorResponseMapper.map(data)
  }

  return { getSelfDrive }
}
